import { createState } from 'shared'
import { DAppService, ForManagingDApps, GuardInput, type UseCase } from 'core'
import { navigate } from 'wouter-preact/use-browser-location'
import { type TargetedEvent } from 'preact/compat'

import {
	addDAppToProjectDetail,
	application,
	emitSystemNotification,
	projectDetailsState,
	resetSystemNotification
} from 'state'

const dAppNameStateContainer = createState('')
const dAppDescriptionStateContainer = createState('')
const selectedUseCasesStateContainer = createState<string[]>([])
const formErrorsStateContainer = createState<
	Record<string, string | undefined>
>({})
const isDAppLoadingStateContainer = createState(false)
const promptStateContainer = createState('')

export const dAppNameState = dAppNameStateContainer.state
export const dAppDescriptionState = dAppDescriptionStateContainer.state
export const selectedUseCasesState = selectedUseCasesStateContainer.state
export const formErrors = formErrorsStateContainer.state
export const isDAppLoading = isDAppLoadingStateContainer.state
export const promptState = promptStateContainer.state

const updateFormErrors = (field: string, error?: string) => {
	formErrorsStateContainer.updateState(prevErrorState => {
		if (prevErrorState[field] === error) return prevErrorState
		return {
			...prevErrorState,
			[field]: error
		}
	})
}

dAppNameStateContainer.onUpdateState(value => {
	const dapps = projectDetailsState.peek()?.dapps || []
	const error = DAppService.ValidateDAppName(value, dapps)

	if (error) {
		updateFormErrors('name', error.inputErrors?.name)
	} else if (formErrors.peek().name !== '') {
		updateFormErrors('name', '')
	}
})

dAppDescriptionStateContainer.onUpdateState(value => {
	const error = GuardInput.check(
		ForManagingDApps.CreateDAppBasePayload.partial(),
		{
			description: value
		}
	)

	if (error) {
		updateFormErrors('description', error.inputErrors?.description)
	} else if (formErrors.peek().description !== '') {
		updateFormErrors('description', '')
	}
})

export const setPromptValue = (value: string) => {
	promptStateContainer.updateState(() => value)
}

export const setDAppName = (value: string) => {
	dAppNameStateContainer.updateState(() => value)
}

export const setDAppDescription = (value: string) => {
	dAppDescriptionStateContainer.updateState(() => value)
}

export const toggleUseCaseSelection = (id: string) => {
	selectedUseCasesStateContainer.updateState(prevState => {
		const index = prevState.indexOf(id)
		if (index === -1) {
			return [...prevState, id]
		}

		return prevState.filter(useCase => useCase !== id)
	})
}

export const resetDAppForm = () => {
	dAppNameStateContainer.updateState(() => '')
	promptStateContainer.updateState(() => '')
	dAppDescriptionStateContainer.updateState(() => '')
	selectedUseCasesStateContainer.updateState(() => [])
	formErrorsStateContainer.updateState(() => ({}))
}

export const getUseCases = async (): Promise<UseCase[]> => {
	const { data, error } = await application.value
		.forManagingDApps()
		.getUseCases()

	if (error) {
		emitSystemNotification({
			error,
			display: 'dialog'
		})

		return []
	}

	return data!
}

export const createDAppFromPrompt = async (
	event: TargetedEvent<HTMLFormElement, Event>
): Promise<void> => {
	event.preventDefault()
	const projectId = projectDetailsState.peek()!.publicId!
	isDAppLoadingStateContainer.updateState(() => true)
	resetSystemNotification()

	const { data, error } = await application.value
		.forManagingDApps()
		.createDAppFromPrompt({
			description: dAppDescriptionState.peek()!,
			name: dAppNameState.peek()!,
			publicProjectId: projectId,
			useCaseIds: selectedUseCasesState.peek(),
			userPrompt: promptState.peek()
		})

	isDAppLoadingStateContainer.updateState(() => false)

	if (error) {
		if (error.inputErrors?.useCaseIds) {
			error.message = error.inputErrors.useCaseIds
		}

		emitSystemNotification({
			error,
			display: 'dialog'
		})

		return
	}

	addDAppToProjectDetail(projectId, data!)
	navigate(`/dapp/${projectId}/smart-contract/${data!.name}/overview`)
	resetDAppForm()
}
